import cn from 'classnames';
import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { FullPageWidth } from '../../components/global/FullPageWidth/FullPageWidth.tsx';
import { SbRespHolidayGiftCollectionLinks } from './SbRespHolidayGiftCollectionLinks.tsx';
import { SbSharedHolidayGiftCollectionVideo } from '../SbSharedHolidayGiftCollection/SbSharedHolidayGiftCollectionVideo.tsx';

// styles
import dibsCss from 'dibs-css';
import styles from './SbRespHolidayGiftCollection.module.css';

// types
import { type SbRespHolidayGiftCollection_itemSearch$key as ItemSearch } from './__generated__/SbRespHolidayGiftCollection_itemSearch.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbRespHolidayGiftCollection_itemSearch on ItemSearchQueryConnection {
        browseContentModule(contentModuleId: $contentModuleId) {
            ...SbRespHolidayGiftCollectionLinks_browseContentModule
        }
    }
`;

const VIMEO_VIDEOID = 1137847453;
const VIDEO_TITLE = 'c07b5ddb-e0be-4189-b6f4-a2132b07c787';
const THUMBNAIL_URL =
    'https://a.1stdibscdn.com/dist/adhoc/holiday-gift-guide/video_thumbnail_11-25-25.jpg?disable=upscale&auto=webp&quality=60';

export const SbRespHolidayGiftCollection: FC<{ itemSearch: ItemSearch }> = ({
    itemSearch: itemSearchRef,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { browseContentModule } = itemSearch || {};

    return (
        <FullPageWidth className={dibsCss.mbMedium}>
            <div
                className={cn(
                    dibsCss.wFull,
                    dibsCss.relative,
                    dibsCss.overflowHidden,
                    styles.container
                )}
            >
                <SbSharedHolidayGiftCollectionVideo
                    width={1920}
                    height={1080}
                    id={VIMEO_VIDEOID}
                    title={VIDEO_TITLE}
                    thumbnailUrl={THUMBNAIL_URL}
                />
                <div
                    className={cn(
                        dibsCss.top0,
                        dibsCss.left0,
                        dibsCss.hFull,
                        dibsCss.wFull,
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.absolute,
                        dibsCss.textCenter,
                        dibsCss.gapSmaller,
                        dibsCss.itemsCenter,
                        dibsCss.justifyCenter,
                        styles.text
                    )}
                >
                    <span className={dibsCss.sassyFontHeaderLargeItalic}>
                        <FormattedMessage
                            id="SbRespHolidayGiftGuide.discover"
                            defaultMessage="Discover The Perfect Gift"
                        />
                    </span>
                    <span
                        className={cn(
                            dibsCss.textCenter,
                            dibsCss.sassyFontHeaderXxLarge,
                            styles.collection
                        )}
                    >
                        <FormattedMessage
                            id="SbRespHolidayGiftGuide.collection"
                            defaultMessage="The Holiday Gift Collection"
                        />
                    </span>
                </div>
            </div>
            {browseContentModule && (
                <SbRespHolidayGiftCollectionLinks browseContentModule={browseContentModule} />
            )}
            {/* using a div with an id for anchor link to scroll to grid-results when clicking on a collection link */}
            <div id="grid-results" />
        </FullPageWidth>
    );
};
